import { inject, Injectable } from '@angular/core'
import User from '@client/User'
import BaseService from '@ui/Services/BaseService'
import { TwoFactorError } from '@client/Errors/TwoFactorError'
import { UsersService } from '@client/Services/UsersService'

export interface TwoFactorQrCode {
	svg: string
	url: string
}

export interface TwoFactorSecret {
	secretKey: string
}

@Injectable()
export class PermissionsService extends BaseService<User> {
	public user: User

	private readonly usersService: UsersService

	constructor() {
		super()
		this.usersService = inject(UsersService)
	}

	public loadUser = async (): Promise<void> => {
		const response = await this.get({ url: '/api/me' })

		this.user = response ? this.usersService.mapToObject(response) : undefined
	}

	public login = async (email: string, password: string): Promise<boolean> => {
		const response = await this.post('/api/login', {
			email,
			password,
		})

		if (response.two_factor) {
			throw new TwoFactorError('2FA required')
		}

		return true
	}

	public twoFactorLogin = async (
		code?: string,
		recoveryCode?: string,
	): Promise<void> => {
		await this.post('/api/two-factor-challenge', {
			code: recoveryCode ? undefined : code,
			recovery_code: recoveryCode,
		})
	}

	public resetPassword = async (
		token: string,
		email: string,
		password: string,
		confirmation: string,
	): Promise<void> => {
		await this.post('/api/reset-password', {
			email,
			password,
			password_confirmation: confirmation,
			token,
		})
	}

	public confirmPassword = async (password: string): Promise<void> => {
		await this.post('/api/user/confirm-password', { password })
	}

	public enableTwoFactor = async (): Promise<void> => {
		await this.post('/api/user/two-factor-authentication', {})
	}

	public getTwoFactorQrCode = async (): Promise<TwoFactorQrCode> => {
		return await this.get({ url: '/api/user/two-factor-qr-code' })
	}

	public getTwoFactorSecret = async (): Promise<TwoFactorSecret> => {
		return await this.get({ url: '/api/user/two-factor-secret-key' })
	}

	public getTwoFactorRecoveryCodes = async (): Promise<string[]> => {
		return await this.get({
			url: '/api/user/two-factor-recovery-codes',
		})
	}

	public confirmTwoFactor = async (code: string): Promise<void> => {
		await this.post('/api/user/confirmed-two-factor-authentication', {
			code,
		})
	}

	public async disableTwoFactor(): Promise<void> {
		await this.delete('/api/user/two-factor-authentication')
	}

	public async regenerateTwoFactorRecoveryCodes(): Promise<void> {
		return await this.post('/api/user/two-factor-recovery-codes', {})
	}

	public async logout(): Promise<void> {
		await this.post('/api/logout', {})
	}
}
